import { Link } from "react-router-dom";
import { ArrowRight, TrendingUp, Leaf, Users, CheckCircle2, Building2, BarChart3, Recycle, Target, Lightbulb, HandshakeIcon, Globe, FileText, Compass } from "lucide-react";
import { Button } from "@/components/ui/button";

const LEVERS = [
  {
    id: "transformer",
    num: "01",
    title: "Transformer son modele economique",
    displayTitle: "Transformer son modèle économique",
    subtitle: "Repenser la création de valeur",
    icon: Recycle,
    color: "#8C9D78",
    contextIcon: BarChart3,
    context: "Face aux limites du modèle linéaire « extraire, produire, jeter », les entreprises doivent repenser leur manière de créer de la valeur. L'économie circulaire, les modèles régénératifs et l'écoconception ne sont plus des options : ce sont les fondations d'une entreprise résiliente et compétitive.",
    description: "Nous accompagnons les organisations dans la transformation de leur modèle économique pour intégrer les principes de durabilité à chaque étape de leur chaîne de valeur. De l'analyse de cycle de vie à la mise en place de boucles vertueuses, nous construisons ensemble un modèle qui concilie performance économique et impact positif.",
    points: [
      "Diagnostic du modèle actuel et identification des leviers de transformation",
      "Intégration des principes d'économie circulaire et d'écoconception",
      "Construction d'une proposition de valeur alignée avec les enjeux environnementaux",
      "Accompagnement dans la mise en place d'indicateurs de suivi et de pilotage",
    ],
  },
  {
    id: "elaborer",
    num: "02",
    title: "Elaborer son plan de transition",
    displayTitle: "Élaborer son plan de transition",
    subtitle: "Structurer sa feuille de route climat",
    icon: Compass,
    color: "#1D3557",
    contextIcon: Globe,
    context: "Le dérèglement climatique impose aux entreprises de se doter d'une trajectoire claire et mesurable. Un plan de transition n'est pas un document de communication : c'est un outil stratégique qui engage l'ensemble de l'organisation vers la neutralité carbone.",
    description: "Nous vous aidons à construire un plan de transition robuste, adossé à la science climatique et aux référentiels reconnus (SBTi, ACT, CSRD). De la mesure de votre empreinte carbone à la définition d'objectifs de réduction ambitieux, nous structurons votre trajectoire de décarbonation avec pragmatisme.",
    points: [
      "Réalisation du bilan carbone (Scopes 1, 2 et 3) selon la méthodologie ADEME",
      "Définition d'objectifs de réduction alignés sur les accords de Paris",
      "Construction d'une feuille de route opérationnelle avec jalons et responsables",
      "Préparation aux exigences réglementaires (CSRD, taxonomie européenne)",
    ],
  },
  {
    id: "engager",
    num: "03",
    title: "Engager ses collaborateurs",
    displayTitle: "Engager ses collaborateurs",
    subtitle: "Mobiliser l'intelligence collective",
    icon: Users,
    color: "#C65D47",
    contextIcon: HandshakeIcon,
    context: "La transition écologique et sociale d'une organisation ne peut réussir sans l'adhésion et l'engagement de ses collaborateurs. Former, sensibiliser et impliquer les équipes est la clé pour ancrer durablement les pratiques responsables dans la culture d'entreprise.",
    description: "Nous concevons des dispositifs de sensibilisation et de formation sur mesure pour embarquer vos équipes dans la transition. Ateliers collaboratifs, fresques, challenges internes : nous créons les conditions de l'engagement collectif pour faire de chaque collaborateur un acteur du changement.",
    points: [
      "Ateliers de sensibilisation (Fresque du Climat, Fresque de l'Économie Circulaire...)",
      "Programmes de formation adaptés aux métiers et aux enjeux de votre secteur",
      "Animation de démarches participatives et de challenges RSE internes",
      "Accompagnement des managers et ambassadeurs du changement",
    ],
  },
];

const StrategyPage = () => {
  return (
    <div className="min-h-screen bg-[#FDFBF7]" data-testid="strategy-page">
      {/* Hero Section */}
      <section className="relative py-20 md:py-28 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#1D3557]/10 via-transparent to-[#8C9D78]/10" />
        <div className="container-custom relative z-10">
          <div className="max-w-3xl mx-auto text-center space-y-8">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#1D3557]/10 text-[#1D3557]">
              <Compass className="w-4 h-4" />
              <span className="text-sm font-medium">Piloter sa stratégie</span>
            </div>
            <h1
              className="text-4xl md:text-5xl lg:text-6xl font-bold text-[#1D3557] leading-tight"
              style={{ fontFamily: "Fraunces, serif" }}
            >
              Construisez une stratégie{" "}
              <span className="text-[#8C9D78]">à la hauteur des enjeux</span>
            </h1>
            <p className="text-lg md:text-xl text-stone-600 max-w-2xl mx-auto">
              Transformer son modèle, planifier sa transition, embarquer ses équipes : trois leviers indissociables pour piloter une stratégie d'entreprise réellement engagée.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                asChild
                className="bg-[#1D3557] hover:bg-[#2A4A6D] text-white rounded-full px-8 h-14 text-lg font-semibold shadow-lg"
                data-testid="strategy-cta-contact"
              >
                <Link to="/contact">
                  Échanger sur votre stratégie
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* 3 Levers Sections */}
      {LEVERS.map((lever, index) => {
        const Icon = lever.icon;
        const ContextIcon = lever.contextIcon;
        const isEven = index % 2 === 0;

        return (
          <section
            key={lever.id}
            className={`py-20 md:py-28 ${isEven ? "bg-[#FDFBF7]" : "bg-[#F4F1EA]"}`}
            data-testid={`lever-${lever.id}`}
          >
            <div className="container-custom">
              {/* Section Header */}
              <div className="max-w-3xl mx-auto text-center mb-16">
                <span
                  className="text-sm font-semibold uppercase tracking-widest"
                  style={{ color: lever.color }}
                >
                  Levier {lever.num}
                </span>
                <h2
                  className="text-3xl md:text-4xl font-bold text-[#1D3557] mt-4 mb-4"
                  style={{ fontFamily: "Fraunces, serif" }}
                >
                  {lever.displayTitle}
                </h2>
                <p className="text-stone-600 text-lg">{lever.subtitle}</p>
              </div>

              <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-start">
                {/* Context Block */}
                <div className={`space-y-8 ${isEven ? "lg:order-1" : "lg:order-2"}`}>
                  {/* Context Card with Pictogram */}
                  <div className="bg-white rounded-2xl p-8 border border-stone-200 shadow-sm">
                    <div className="flex items-center gap-4 mb-6">
                      <div
                        className="w-14 h-14 rounded-2xl flex items-center justify-center flex-shrink-0"
                        style={{ backgroundColor: `${lever.color}15` }}
                      >
                        <ContextIcon className="w-7 h-7" style={{ color: lever.color }} />
                      </div>
                      <h3
                        className="text-xl font-semibold text-[#1D3557]"
                        style={{ fontFamily: "Fraunces, serif" }}
                      >
                        Contexte
                      </h3>
                    </div>
                    <p className="text-stone-600 leading-relaxed">{lever.context}</p>
                  </div>
                </div>

                {/* Description + Points Block */}
                <div className={`space-y-8 ${isEven ? "lg:order-2" : "lg:order-1"}`}>
                  <div className="flex items-center gap-4 mb-2">
                    <div
                      className="w-14 h-14 rounded-2xl flex items-center justify-center flex-shrink-0"
                      style={{ backgroundColor: `${lever.color}15` }}
                    >
                      <Icon className="w-7 h-7" style={{ color: lever.color }} />
                    </div>
                    <h3
                      className="text-xl font-semibold text-[#1D3557]"
                      style={{ fontFamily: "Fraunces, serif" }}
                    >
                      Notre accompagnement
                    </h3>
                  </div>

                  <p className="text-stone-600 leading-relaxed">{lever.description}</p>

                  <ul className="space-y-4">
                    {lever.points.map((point, i) => (
                      <li key={i} className="flex items-start gap-3">
                        <CheckCircle2
                          className="w-5 h-5 flex-shrink-0 mt-0.5"
                          style={{ color: lever.color }}
                        />
                        <span className="text-stone-700">{point}</span>
                      </li>
                    ))}
                  </ul>

                  <Button
                    asChild
                    className="rounded-full px-8 h-12 text-white border-0 hover:opacity-90"
                    style={{ backgroundColor: lever.color }}
                    data-testid={`contact-lever-${lever.id}`}
                  >
                    <Link to="/contact">
                      Discuter de ce levier
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Link>
                  </Button>
                </div>
              </div>
            </div>
          </section>
        );
      })}

      {/* CTA Section */}
      <section className="py-20 bg-[#1D3557] relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-[#8C9D78] rounded-full blur-[150px]" />
          <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-[#C65D47] rounded-full blur-[150px]" />
        </div>
        <div className="container-custom relative z-10 text-center">
          <h2
            className="text-3xl md:text-4xl font-bold text-white mb-6"
            style={{ fontFamily: "Fraunces, serif" }}
          >
            Prêt à piloter votre transition ?
          </h2>
          <p className="text-xl text-white/70 mb-10 max-w-2xl mx-auto">
            Chaque organisation est unique. Discutons de votre contexte pour construire ensemble une stratégie ambitieuse et réaliste.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              asChild
              className="bg-[#8C9D78] hover:bg-[#7A8B69] text-white rounded-full px-10 h-14 text-lg font-semibold"
              data-testid="strategy-final-cta"
            >
              <Link to="/contact">
                Prendre rendez-vous
                <ArrowRight className="w-5 h-5 ml-2" />
              </Link>
            </Button>
            <Button
              asChild
              variant="outline"
              className="border-2 border-white text-white hover:bg-white hover:text-[#1D3557] rounded-full px-10 h-14 text-lg"
            >
              <a href="tel:+33751131092">07 51 13 10 92</a>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default StrategyPage;
